import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import { toast } from 'react-toastify';
import { getImageSignedUrl } from '../../utils/signedUrl';
import profile_img from '../../assets/images/pages/defaultImage.jpg';
import '../../assets/styles/pages.scss';

interface StoreDocument {
    _id: string;
    type: string;
    images?: {
        front?: string;
        back?: string;
    };
}

interface StoreData {
    _id: string;
    name: string;
    email: string;
    country_code: string;
    phone_number: number;
    image: string | null;
    company: string;
    country: string;
    state: string;
    city: string;
    pin_code: string;
    apartment_number: string;
    full_address: string;
    location: {
        type: string;
        coordinates: [number, number];
    };
    account_status: string;
    is_admin_verified: 'approve' | 'pending' | 'reject';
    documents: StoreDocument[];
    language: string;
    role: string;
    master_seller_id: string;
    delivery_provide: boolean;
    admin_commission: number | null;
    rejection_reason?: string;
    is_blocked: boolean;
    is_deleted: boolean;
    created_at: number;
    updated_at: number;
}

const StoreProfile = () => {
    const { language_data, authState, loading, setLoading } = useContext(GlobalContext);
    const [storeData, setStoreData] = useState<StoreData | null>(null);
    const [profileImageUrl, setProfileImageUrl] = useState<string>(profile_img);
    const [documentUrls, setDocumentUrls] = useState<{ [key: string]: { front?: string; back?: string } }>({});
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordData, setPasswordData] = useState({
        // currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);


    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error(
                language_data?.[authState?.lang]?.common_password_mismatch ||
                'New password and confirm password do not match'
            );
            return;
        }
        setPasswordLoading(true);
        try {
            const info = {
                // currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            };
            await henceforthApi.Store.changePassword(storeData!._id, info);
            toast.success(
                language_data?.[authState?.lang]?.common_password_changed ||
                'Password changed successfully'
            );
            setShowPasswordModal(false);
            setPasswordData({ newPassword: '', confirmPassword: '' });
        } catch (error: any) {
            console.error('Error changing password:', error);
            toast.error(error?.response?.data?.message || 'Failed to change password');
        } finally {
            setPasswordLoading(false);
        }
    };

    // Fetch store details
    const fetchStoreDetail = async () => {
        if (!id) {
            toast.error('Store ID is required');
            return;
        }

        setLoading(true);
        try {
            const language = authState?.lang || 'ENGLISH';
            const response = await henceforthApi.Store.getStoreDetail(id);

            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                const store = response.data[0];
                setStoreData(store);

                // Check if the logged-in user is the owner of this store
                if (authState?._id && store.master_seller_id === authState._id) {
                    setIsOwner(true);
                }
            } else {
                toast.error('Store not found');
            }
        } catch (error: any) {
            console.error('Error fetching store details:', error);
            // handleError(error, 'active', authDispatch);
        } finally {
            setLoading(false);
        }
    };

    // Load store profile image
    useEffect(() => {
        const loadProfileImage = async () => {
            if (!storeData?.image) {
                setProfileImageUrl(profile_img);
                return;
            }

            try {
                const fileKey = storeData.image.endsWith('.webp')
                    ? `original/${storeData.image}`
                    : `medium/${storeData.image}`;
                const signedUrl = await getImageSignedUrl(fileKey);
                setProfileImageUrl(signedUrl || profile_img);
            } catch (error) {
                console.error('Error loading store image:', error);
                setProfileImageUrl(profile_img);
            }
        };

        if (storeData) {
            loadProfileImage();
        }
    }, [storeData?.image]);

    // Load document images
    useEffect(() => {
        const loadDocumentImages = async () => {
            if (!storeData?.documents || !Array.isArray(storeData.documents)) {
                return;
            }

            const urls: { [key: string]: { front?: string; back?: string } } = {};

            for (const doc of storeData.documents) {
                if (doc.images && typeof doc.images === 'object') {
                    urls[doc._id] = {};

                    if (doc.images.front) {
                        try {
                            const fileKey = doc.images.front.endsWith('.webp')
                                ? `original/${doc.images.front}`
                                : `medium/${doc.images.front}`;
                            const signedUrl = await getImageSignedUrl(fileKey);
                            urls[doc._id].front = signedUrl || profile_img;
                        } catch (error) {
                            console.error('Error loading front document image:', error);
                            urls[doc._id].front = profile_img;
                        }
                    }

                    if (doc.images.back) {
                        try {
                            const fileKey = doc.images.back.endsWith('.webp')
                                ? `original/${doc.images.back}`
                                : `medium/${doc.images.back}`;
                            const signedUrl = await getImageSignedUrl(fileKey);
                            urls[doc._id].back = signedUrl || profile_img;
                        } catch (error) {
                            console.error('Error loading back document image:', error);
                            urls[doc._id].back = profile_img;
                        }
                    }
                }
            }

            setDocumentUrls(urls);
        };

        if (storeData) {
            loadDocumentImages();
        }
    }, [storeData?.documents]);

    // Fetch data on mount
    useEffect(() => {
        fetchStoreDetail();
    }, [id]);


    // Format date
    const formatDate = (timestamp: number) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Get status badge class
    const getStatusBadgeClass = (status: string) => {
        const statusMap: { [key: string]: string } = {
            ACTIVATED: 'badge-success',
            DEACTIVATED: 'badge-danger',
            SUSPENDED: 'badge-warning',
            PENDING: 'badge-warning',
        };
        return statusMap[status] || 'badge-secondary';
    };

    // Get verification badge class
    const getVerificationBadgeClass = (status: string) => {
        const statusMap: { [key: string]: string } = {
            approve: 'badge-success',
            pending: 'badge-warning',
            reject: 'badge-danger',
        };
        return statusMap[status] || 'badge-secondary';
    };

    const getVerificationLabel = (status: string) => {
        const labelMap: { [key: string]: string } = {
            approve: 'Verified',
            pending: 'Pending',
            reject: 'Rejected',
        };
        return labelMap[status] || status;
    };

    if (loading && !storeData) {
        return (
            <div className="page-spacing">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border text-theme" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading store details...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!storeData) {
        return (
            <div className="page-spacing">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 text-center py-5">
                            <i className="fa fa-store fa-4x text-muted mb-3"></i>
                            <h4>Store Not Found</h4>
                            <p className="text-muted">The store you're looking for doesn't exist or has been removed.</p>
                            <Link to="/" className="btn btn-theme mt-3">
                                <i className="fa fa-arrow-left me-2"></i>Go Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            {/* Breadcrumb */}
            <section className="breadcrum-box">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <h2>{language_data?.[authState?.lang]?.common_store_profile || 'Store Profile'}</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item">
                                        <Link to="/">{language_data?.[authState?.lang]?.common_home || 'Home'}</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/store">{language_data?.[authState?.lang]?.common_stores || 'Stores'}</Link>
                                    </li>
                                    <li className="breadcrumb-item active fw-bold">{storeData.name}</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            {/* Page Content */}
            <div className="page-spacing">
                <section className="seller-profile">
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            {/* Status Alerts */}
                            <div className="col-12 mb-4">
                                {storeData.is_admin_verified === 'reject' && (
                                    <div
                                        className="alert alert-danger p-4 rounded-3 border-0 shadow-lg"
                                        role="alert"
                                        style={{ borderLeft: '5px solid #dc3545' }}
                                    >
                                        <div className="d-flex align-items-start">
                                            <div className="alert-icon-wrapper me-3 flex-shrink-0">
                                                <div
                                                    className="alert-icon-circle bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '60px', height: '60px' }}
                                                >
                                                    <i className="fa fa-times-circle text-danger" style={{ fontSize: '32px' }}></i>
                                                </div>
                                            </div>
                                            <div className="alert-content flex-grow-1">
                                                <h5 className="alert-heading fw-bold mb-2 text-danger">
                                                    {language_data?.[authState?.lang]?.common_verification_rejected || 'Verification Rejected'}
                                                </h5>
                                                <p className="mb-3 text-muted">
                                                    {language_data?.[authState?.lang]?.common_store_rejected_message ||
                                                        'This store profile has been rejected by the administrator. Please review the reason below.'}
                                                </p>

                                                {storeData.rejection_reason && (
                                                    <div className="rejection-reason-box bg-white rounded-3 p-3 mb-3 border-start border-4 border-danger">
                                                        <h6 className="fw-bold mb-2 text-dark">
                                                            <i className="fa fa-info-circle text-danger me-2"></i>
                                                            {language_data?.[authState?.lang]?.common_rejection_reason || 'Rejection Reason:'}
                                                        </h6>
                                                        <p className="mb-0 text-dark">{storeData.rejection_reason}</p>
                                                    </div>
                                                )}

                                                {isOwner && (
                                                    <div className="alert-actions d-flex flex-wrap gap-2 mt-3">
                                                        <button
                                                            className="btn btn-danger px-4 py-2 fw-semibold"
                                                            onClick={() => navigate(`/store/edit/${storeData._id}`)}
                                                        >
                                                            <i className="fa fa-edit me-2"></i>
                                                            {language_data?.[authState?.lang]?.common_edit_resubmit || 'Edit & Resubmit'}
                                                        </button>

                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {storeData.is_admin_verified === 'pending' && (
                                    <div
                                        className="alert alert-warning p-4 rounded-3 border-0 shadow-sm d-flex align-items-start"
                                        role="alert"
                                        style={{ borderLeft: '5px solid #ffc107' }}
                                    >
                                        <i className="fa fa-clock-o fa-3x me-3 text-warning flex-shrink-0"></i>
                                        <div>
                                            <h5 className="alert-heading fw-bold mb-1">
                                                {language_data?.[authState?.lang]?.common_verification_pending || 'Verification Pending'}
                                            </h5>
                                            <p className="mb-0">
                                                {language_data?.[authState?.lang]?.common_store_pending_message ||
                                                    'This store profile is currently under review by our administrators.'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {storeData.account_status === 'DEACTIVATED' && (
                                    <div
                                        className="alert alert-danger p-4 rounded-3 border-0 shadow-sm d-flex align-items-start"
                                        role="alert"
                                        style={{ borderLeft: '5px solid #dc3545' }}
                                    >
                                        <i className="fa fa-exclamation-triangle fa-3x me-3 text-danger flex-shrink-0"></i>
                                        <div>
                                            <h5 className="alert-heading fw-bold mb-1">
                                                {language_data?.[authState?.lang]?.common_account_deactivated || 'Account Deactivated'}
                                            </h5>
                                            <p className="mb-0">
                                                {language_data?.[authState?.lang]?.common_account_deactivated_message ||
                                                    'This store account has been deactivated. Please contact support for more information.'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {storeData.is_blocked && (
                                    <div
                                        className="alert alert-danger p-4 rounded-3 border-0 shadow-sm d-flex align-items-start"
                                        role="alert"
                                        style={{ borderLeft: '5px solid #dc3545' }}
                                    >
                                        <i className="fa fa-ban fa-3x me-3 text-danger flex-shrink-0"></i>
                                        <div>
                                            <h5 className="alert-heading fw-bold mb-1">
                                                {language_data?.[authState?.lang]?.common_account_blocked || 'Account Blocked'}
                                            </h5>
                                            <p className="mb-0">
                                                {language_data?.[authState?.lang]?.common_account_blocked_message ||
                                                    'This store account has been blocked. Please contact support for more information.'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Store Profile Card */}
                            <div className="col-sm-9 col-md-5 col-lg-4 col-xl-4 col-xxl-3 px-0">
                                <div className="common-card h-100">
                                    <div className="common-card-title d-flex justify-content-between align-items-center">
                                        <h5>{language_data?.[authState?.lang]?.common_store_profile || 'Store Profile'}</h5>
                                        <span className={`badge ${getVerificationBadgeClass(storeData.is_admin_verified)} px-3 py-2`}>
                                            {getVerificationLabel(storeData.is_admin_verified)}
                                        </span>
                                    </div>

                                    <div className="common-card-content">
                                        {/* Store Logo */}
                                        <div className="profile-image">
                                            <img src={profileImageUrl} alt={storeData.name} className="" />
                                        </div>

                                        {/* Store Details */}
                                        <div className="profile-image my-4">
                                            <h5 className="mb-2 fw-bold">{storeData.name}</h5>
                                            {storeData.company && (
                                                <p className="d-flex align-items-center mb-2">
                                                    <i className="fa fa-building me-2 fs-5"></i>
                                                    {storeData.company}
                                                </p>
                                            )}
                                            <p className="d-flex align-items-center mb-1">
                                                <i className="fa fa-envelope me-2 fs-5"></i>
                                                {storeData.email}
                                            </p>
                                            <p className="d-flex align-items-center mt-2">
                                                <i className="fa fa-phone-square me-2 fs-5"></i>
                                                +{storeData.country_code} {storeData.phone_number}
                                            </p>

                                            {storeData.full_address && (
                                                <p className="d-flex align-items-start mt-2">
                                                    <i className="fa fa-map-marker me-2 fs-5 mt-1"></i>
                                                    <span>
                                                        {storeData.full_address}
                                                        {storeData.apartment_number && (
                                                            <span className="d-block small">
                                                                {language_data?.[authState?.lang]?.common_apartment || 'Apartment'}: {storeData.apartment_number}
                                                            </span>
                                                        )}
                                                        <span className="d-block small">
                                                            {storeData.city}, {storeData.state}, {storeData.country} - {storeData.pin_code}
                                                        </span>
                                                    </span>
                                                </p>
                                            )}

                                            {storeData.location?.coordinates && (
                                                <p className="d-flex align-items-center my-2">
                                                    <i className="fa fa-globe me-2 fs-5"></i>
                                                    <span>
                                                        Lat: {storeData.location.coordinates[1]}, Lng: {storeData.location.coordinates[0]}
                                                    </span>
                                                </p>
                                            )}
                                        </div>


                                        {/* Member Since */}
                                        <div className="member-since text-muted small text-center mb-3">
                                            <i className="fa fa-calendar me-1"></i>
                                            {language_data?.[authState?.lang]?.common_member_since || 'Member since'}: {formatDate(storeData.created_at)}
                                        </div>

                                        {/* Action Buttons */}
                                        {isOwner && (
                                            <div className="profile-button">
                                                <Link
                                                    to={`/store/edit/${storeData._id}`}
                                                    className="btn btn-theme w-100"
                                                >
                                                    <i className="fa fa-edit me-2"></i>
                                                    {language_data?.[authState?.lang]?.common_edit_profile || 'Edit Profile'}
                                                </Link>
                                            </div>
                                        )}

                                        <button
                                            className="btn btn-theme w-100 mt-2"
                                            onClick={() => setShowPasswordModal(true)}
                                        >
                                            <i className="fa fa-key me-2"></i>
                                            {language_data?.[authState?.lang]?.common_change_password || 'Change Password'}
                                        </button>

                                        {!isOwner && (
                                            <div className="profile-button">
                                                <button
                                                    className="btn btn-outline-theme w-100"
                                                    onClick={() => navigate(`/stores/${storeData._id}/products`)}
                                                >
                                                    <i className="fa fa-shopping-bag me-2"></i>
                                                    {language_data?.[authState?.lang]?.common_view_products || 'View Products'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Documents Section */}
                            {storeData.documents && Array.isArray(storeData.documents) && storeData.documents.length > 0 && (
                                <div className="col-12 px-0 mt-4">
                                    <div className="common-card">
                                        <div className="common-card-title">
                                            <h5>
                                                <i className="fa fa-file-text-o me-2 text-theme"></i>
                                                {language_data?.[authState?.lang]?.common_documents || 'Documents'}
                                            </h5>
                                        </div>
                                        <div className="common-card-content">
                                            <div className="row">
                                                {storeData.documents.map((doc: StoreDocument) => (
                                                    <div className="col-md-6 col-lg-4 mb-4" key={doc._id}>
                                                        <h6 className="text-capitalize fw-bold mb-3">
                                                            {doc.type.replace(/_/g, ' ')}
                                                        </h6>
                                                        <div className="row g-2">
                                                            {doc.images && typeof doc.images === 'object' && (
                                                                <>
                                                                    {doc.images.front && (
                                                                        <div className="col-6">
                                                                            <div
                                                                                className="document-image-wrapper border rounded overflow-hidden"
                                                                                style={{ height: '150px' }}
                                                                            >
                                                                                <img
                                                                                    src={documentUrls[doc._id]?.front || profile_img}
                                                                                    alt={`${doc.type} front`}
                                                                                    className="w-100 h-100"
                                                                                    style={{ objectFit: 'cover', cursor: 'pointer' }}
                                                                                    onClick={() =>
                                                                                        window.open(
                                                                                            documentUrls[doc._id]?.front,
                                                                                            '_blank'
                                                                                        )
                                                                                    }
                                                                                />
                                                                                <small className="text-muted d-block text-center mt-1">
                                                                                    {language_data?.[authState?.lang]?.common_front || 'Front'}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {doc.images.back && (
                                                                        <div className="col-6">
                                                                            <div
                                                                                className="document-image-wrapper border rounded overflow-hidden"
                                                                                style={{ height: '150px' }}
                                                                            >
                                                                                <img
                                                                                    src={documentUrls[doc._id]?.back || profile_img}
                                                                                    alt={`${doc.type} back`}
                                                                                    className="w-100 h-100"
                                                                                    style={{ objectFit: 'cover', cursor: 'pointer' }}
                                                                                    onClick={() =>
                                                                                        window.open(
                                                                                            documentUrls[doc._id]?.back,
                                                                                            '_blank'
                                                                                        )
                                                                                    }
                                                                                />
                                                                                <small className="text-muted d-block text-center mt-1">
                                                                                    {language_data?.[authState?.lang]?.common_back || 'Back'}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>

            {showPasswordModal && (
                <div
                    className="modal show d-block"
                    tabIndex={-1}
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowPasswordModal(false);
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {language_data?.[authState?.lang]?.common_change_password || 'Change Password'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPasswordModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleChangePassword}>
                                <div className="modal-body">
                                    {/* <div className="mb-3">
                                        <label className="form-label">
                                            {language_data?.[authState?.lang]?.common_current_password || 'Current Password'}
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={passwordData.currentPassword}
                                            onChange={(e) =>
                                                setPasswordData({
                                                    ...passwordData,
                                                    currentPassword: e.target.value,
                                                })
                                            }
                                            required
                                        />
                                    </div> */}
                                    <div className="mb-3">
                                        <label className="form-label">
                                            {language_data?.[authState?.lang]?.common_new_password || 'New Password'}
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={passwordData.newPassword}
                                            onChange={(e) =>
                                                setPasswordData({
                                                    ...passwordData,
                                                    newPassword: e.target.value,
                                                })
                                            }
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            {language_data?.[authState?.lang]?.common_confirm_password || 'Confirm New Password'}
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) =>
                                                setPasswordData({
                                                    ...passwordData,
                                                    confirmPassword: e.target.value,
                                                })
                                            }
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowPasswordModal(false)}
                                    >
                                        {language_data?.[authState?.lang]?.common_cancel || 'Cancel'}
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-theme"
                                        disabled={passwordLoading}
                                    >
                                        {passwordLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {language_data?.[authState?.lang]?.common_changing || 'Changing...'}
                                            </>
                                        ) : (
                                            language_data?.[authState?.lang]?.common_change_password || 'Change Password'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default StoreProfile;